import { useId } from 'react';
import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { Box, Button, Drawer, IconButton, Stack, Typography } from '@mui/material';

import type { GeneratedIntakeHelpPanel } from '../../types';

interface HelpPanelDrawerProps {
  drawerId: string;
  panel: GeneratedIntakeHelpPanel | null;
  open: boolean;
  onClose: () => void;
  onAction: (action: GeneratedIntakeHelpPanel['support_cta']['action']) => void;
}

export function HelpPanelDrawer({
  drawerId,
  panel,
  open,
  onClose,
  onAction,
}: HelpPanelDrawerProps) {
  const id = useId().replace(/:/g, '');
  const titleId = `intake-help-title-${id}`;
  const definitionId = `intake-help-definition-${id}`;

  if (!panel) return null;

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          id: drawerId,
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': titleId,
          'aria-describedby': definitionId,
          sx: { width: { xs: 'min(100vw, 420px)', sm: 420 }, p: 3 },
        },
      }}
    >
      <Stack sx={{ height: '100%' }} spacing={2}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography id={titleId} component="h2" variant="h6">
            {panel.title}
          </Typography>
          <IconButton autoFocus aria-label="Close help" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box id={definitionId}>
          <SimpleMarkdownRenderer markdown={panel.definition} sx={{ p: 0 }} />
        </Box>

        {(panel.what_goes_wrong?.length ?? 0) > 0 && (
          <Box>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 0.5 }}>
              What can go wrong
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
              {panel.what_goes_wrong?.map((item, index) => (
                <Box component="li" key={`${panel.key}-pitfall-${index}`}>
                  <SimpleMarkdownRenderer markdown={item} sx={{ p: 0 }} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {panel.where_to_find && (
          <Box>
            <Typography component="h3" variant="subtitle2" sx={{ mb: 0.5 }}>
              Where to find it
            </Typography>
            <SimpleMarkdownRenderer markdown={panel.where_to_find} sx={{ p: 0 }} />
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onAction(panel.support_cta.action);
          }}
        >
          {panel.support_cta.label}
        </Button>
      </Stack>
    </Drawer>
  );
}

interface GroupHelpTriggerProps {
  groupName: string;
  controlsId?: string;
  expanded: boolean;
  onOpen: (opener: HTMLButtonElement) => void;
}

export function GroupHelpTrigger({
  groupName,
  controlsId,
  expanded,
  onOpen,
}: GroupHelpTriggerProps) {
  return (
    <IconButton
      size="small"
      aria-label={`Help for ${groupName}`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={expanded ? controlsId : undefined}
      onClick={event => onOpen(event.currentTarget)}
    >
      <HelpOutlineIcon fontSize="small" />
    </IconButton>
  );
}
